import React, { useMemo, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { GatsbyImage } from 'gatsby-plugin-image'
import AddToCartButton from 'src/components/AddToCartButton'
import { FormattedCurrency } from 'src/utils/FormattedCurrency'
import {
  STYLE_CONFIG as CALENDAR_SNAP_STYLE_CONFIG,
  normalizeCalendarSnapFrameColor,
} from 'src/templates/CalendarSnapFramePage/constants'
import { CAL_2_STYLE_OOS } from 'src/templates/CalendarProductPage/styleConfig'
import Analytics from 'src/utils/Analytics'
import {
  Wrapper,
  Label,
  Card,
  Content,
  TopRow,
  ImageWrapper,
  DiscountBadge,
  TextInfo,
  Title,
  Subtitle,
  Controls,
  StyleButtonsRow,
  ColorRow,
  StyleButton,
  Swatches,
  SwatchButton,
  ColorName,
  CtaCol,
  PriceContainer,
  OriginalPrice,
  DiscountedPrice,
} from './styles'

const PRODUCT_CONFIG = {
  calendar2: {
    title: 'Calendar 2 Snap Frame',
    styleOrder: ['Classic', 'Shadow Box'],
    colorOrder: {
      Classic: ['White', 'Black', 'Sage', 'Lagoon'],
      'Shadow Box': ['Charcoal', 'Silver'],
    },
  },
  calendarMax: {
    title: 'Calendar Max Snap Frame',
    styleOrder: ['Wood', 'Classic', 'Shadow Box'],
    colorOrder: {
      Classic: ['White', 'Black'],
      'Shadow Box': ['Charcoal', 'Silver'],
      Wood: ['Walnut', 'Birch'],
    },
  },
}

const COLOR_TO_BACKGROUND = Object.values(CALENDAR_SNAP_STYLE_CONFIG).reduce(
  (colorMap, styleConfig) => {
    Object.values(styleConfig?.colorsByDevice || {}).forEach((colors = []) => {
      colors.forEach(({ name, swatch }) => {
        if (!colorMap[name] && swatch) {
          colorMap[name] = swatch
        }
      })
    })
    return colorMap
  },
  {}
)

const normalizeColor = (color = '') => normalizeCalendarSnapFrameColor(color)

const normalizeStyle = (style = '') => {
  if (style.toLowerCase() === 'shadowbox') return 'Shadow Box'
  return style
}

/** LAN-1481 A/B analytics (Linear): `Added {size} {style} {color} Calendar Snap Frame Cross Sell Deal to Cart` */
const analyticsCrossSellDealAddedEvent = (size, style, color) =>
  `Added ${size} ${style} ${color} Calendar Snap Frame Cross Sell Deal to Cart`

/**
 * `Purchased {size} {style} {color} Calendar Snap Frame Cross Sell Deal` must fire on order
 * completion (Shopify checkout / server or GTM) — not in this Gatsby app on add-to-cart.
 */

const extractCartCalendarContext = (cartLines = []) => {
  const calendarLine = cartLines.find(({ node }) => {
    const title = node?.merchandise?.product?.title || ''
    return title.includes('Calendar 2') || title.includes('Calendar Max')
  })

  if (!calendarLine) {
    return null
  }

  const lineTitle = calendarLine?.node?.merchandise?.product?.title || ''
  const isCalendarMax = lineTitle.includes('Calendar Max')
  const productKey = isCalendarMax ? 'calendarMax' : 'calendar2'
  const variantTitle = calendarLine?.node?.merchandise?.title || ''
  const sourceText = `${lineTitle} ${variantTitle}`
  const match = sourceText.match(/\(([^)]+)\)/)

  let style = null
  let color = null

  if (match && match[1]) {
    const [parsedStyle, parsedColor] = match[1].split('/').map((item) => item.trim())
    style = parsedStyle || null
    color = normalizeColor(parsedColor)
  }

  return {
    productKey,
    style,
    color,
  }
}

const buildVariantCatalog = (snapProducts = {}) =>
  Object.entries(snapProducts).reduce((catalog, [productKey, product]) => {
    if (!product?.variants?.length) return catalog

    const productImageData = product?.images?.[0]?.gatsbyImageData
    const productImageSrc = productImageData?.images?.fallback?.src || ''
    const variantsByStyle = product.variants.reduce((styles, variant) => {
      const [rawStyle, rawColor] = (variant?.title || '').split('/').map((part) => part?.trim())
      if (!rawStyle || !rawColor) return styles

      const style = normalizeStyle(rawStyle)
      const color = normalizeColor(rawColor)
      if (productKey === 'calendar2' && CAL_2_STYLE_OOS?.[style]?.[color]) {
        return styles
      }
      const variantImageSrc = productImageData?.images?.fallback?.src || productImageSrc

      if (!styles[style]) styles[style] = {}
      styles[style][color] = {
        ...variant,
        imageData: productImageData,
        imageSrc: variantImageSrc,
      }
      return styles
    }, {})

    catalog[productKey] = variantsByStyle
    return catalog
  }, {})

const CalendarSnapFrameCrossSell = ({
  cartLines,
  snapProducts,
  variantImageMap,
  fallbackImageData,
  handleAddToCart,
  adding,
}) => {
  const cartContext = useMemo(() => extractCartCalendarContext(cartLines), [cartLines])
  const variantCatalog = useMemo(() => buildVariantCatalog(snapProducts), [snapProducts])
  const [selectedStyle, setSelectedStyle] = useState(null)
  const [selectedColor, setSelectedColor] = useState(null)
  const [hoverStyle, setHoverStyle] = useState(null)
  const [hoverColor, setHoverColor] = useState(null)
  const [isAddingSelection, setIsAddingSelection] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const lastInitializedProductKeyRef = useRef(null)
  const isMountedRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const updateDesktop = () => setIsDesktop(window.innerWidth >= 768)
    updateDesktop()
    window.addEventListener('resize', updateDesktop)
    return () => window.removeEventListener('resize', updateDesktop)
  }, [])

  const productConfig = cartContext ? PRODUCT_CONFIG[cartContext.productKey] : null
  const availableVariantsByStyle = cartContext ? variantCatalog[cartContext.productKey] : null
  const styleKeys = useMemo(
    () =>
      productConfig
        ? productConfig.styleOrder.filter((styleKey) =>
            Boolean(availableVariantsByStyle?.[styleKey])
          )
        : [],
    [productConfig, availableVariantsByStyle]
  )
  const colorKeysByStyle = useMemo(() => {
    if (!productConfig || !availableVariantsByStyle) return {}

    return styleKeys.reduce((acc, styleKey) => {
      const availableColors = Object.keys(availableVariantsByStyle[styleKey] || {})
      const orderedColors = (productConfig.colorOrder?.[styleKey] || []).filter((colorKey) =>
        availableColors.includes(colorKey)
      )
      const fallbackColors = availableColors.filter((colorKey) => !orderedColors.includes(colorKey))

      acc[styleKey] = [...orderedColors, ...fallbackColors]
      return acc
    }, {})
  }, [productConfig, availableVariantsByStyle, styleKeys])

  useEffect(() => {
    if (!productConfig || !cartContext?.productKey) return
    const { productKey } = cartContext
    const productChanged = lastInitializedProductKeyRef.current !== productKey
    const hasExistingSelection = Boolean(selectedStyle && selectedColor)

    // Prevent flicker on cart updates after "Get the deal":
    // keep the current click-selected option unless the product context changes.
    if (hasExistingSelection && !productChanged) return

    const cartStyle = cartContext?.style
    const cartColor = cartContext?.color

    const firstOption = styleKeys
      .flatMap((styleKey) =>
        (colorKeysByStyle[styleKey] || []).map((colorKey) => ({ styleKey, colorKey }))
      )
      .find(({ styleKey, colorKey }) => !(styleKey === cartStyle && colorKey === cartColor))

    const fallbackOption = styleKeys.flatMap((styleKey) =>
      (colorKeysByStyle[styleKey] || []).map((colorKey) => ({ styleKey, colorKey }))
    )[0]

    const initialOption = firstOption || fallbackOption
    if (!initialOption) return

    setSelectedStyle(initialOption.styleKey)
    setSelectedColor(initialOption.colorKey)
    setHoverStyle(null)
    setHoverColor(null)
    lastInitializedProductKeyRef.current = productKey
  }, [productConfig, cartContext, styleKeys, colorKeysByStyle, selectedStyle, selectedColor])

  const crossSellViewTrackedRef = useRef(null)

  useEffect(() => {
    if (!cartContext?.productKey || !productConfig || !selectedStyle || !selectedColor) return
    const variantForView = variantCatalog[cartContext.productKey]?.[selectedStyle]?.[selectedColor]
    if (!variantForView?.shopifyId) return
    if (crossSellViewTrackedRef.current === cartContext.productKey) return
    crossSellViewTrackedRef.current = cartContext.productKey
    const productLabel = cartContext.productKey === 'calendarMax' ? 'Calendar Max' : 'Calendar 2'
    Analytics.track(`Viewed Cart ${productLabel} Snap Frame Cross Sell`)
  }, [cartContext?.productKey, productConfig, selectedStyle, selectedColor, variantCatalog])

  if (!productConfig || !selectedStyle || !selectedColor) {
    return null
  }

  const selectedVariant = availableVariantsByStyle?.[selectedStyle]?.[selectedColor] || null
  const previewStyle = hoverStyle || selectedStyle
  const previewColor = hoverColor || selectedColor
  const previewVariant = availableVariantsByStyle?.[previewStyle]?.[previewColor] || null
  const previewImageDataFromMap =
    variantImageMap?.[cartContext?.productKey]?.[previewStyle]?.[previewColor]
  const selectedPreviewImageData =
    variantImageMap?.[cartContext?.productKey]?.[selectedStyle]?.[selectedColor]
  const selectedImageData =
    selectedPreviewImageData || selectedVariant?.imageData || fallbackImageData
  const previewImageData = previewImageDataFromMap || previewVariant?.imageData || selectedImageData

  if (!selectedVariant?.shopifyId) {
    return null
  }

  const originalPrice = Number(selectedVariant.price || 0)
  const discountedPrice = Number((originalPrice * 0.9).toFixed(2))
  const colorsForSelectedStyle = colorKeysByStyle[selectedStyle] || []
  const isButtonAdding = Boolean(adding || isAddingSelection)

  const calendarLineLabel = cartContext.productKey === 'calendarMax' ? 'Calendar Max' : 'Calendar 2'

  const handleStyleChange = (styleKey) => {
    Analytics.track(`Clicked Cart ${calendarLineLabel} Snap Frame Cross Sell ${styleKey} Style CTA`)
    setSelectedStyle(styleKey)
    const nextColor = colorKeysByStyle[styleKey]?.[0]
    if (nextColor) {
      setSelectedColor(nextColor)
    }
    setHoverStyle(null)
    setHoverColor(null)
  }

  const handleGetDealClick = async () => {
    if (isButtonAdding || !selectedVariant?.shopifyId) return
    setIsAddingSelection(true)
    try {
      Analytics.track(
        analyticsCrossSellDealAddedEvent(calendarLineLabel, selectedStyle, selectedColor)
      )
      await handleAddToCart(selectedVariant.shopifyId)
    } finally {
      if (isMountedRef.current) {
        setIsAddingSelection(false)
      }
    }
  }

  return (
    <Wrapper>
      <Label>Bonus Offer for Calendar Shoppers</Label>
      <Card>
        <Content>
          <TopRow>
            <ImageWrapper>
              <DiscountBadge>10% Off</DiscountBadge>
              {previewImageData ? (
                <GatsbyImage
                  image={previewImageData}
                  alt={`${previewStyle} ${previewColor} Snap Frame`}
                />
              ) : null}
            </ImageWrapper>
            <TextInfo>
              <Title>{productConfig.title}</Title>
              <Subtitle>Choose another style to mix & match.</Subtitle>
              <PriceContainer hideOnDesktop>
                <OriginalPrice>{FormattedCurrency(originalPrice)}</OriginalPrice>
                <DiscountedPrice>{FormattedCurrency(discountedPrice)}</DiscountedPrice>
              </PriceContainer>
            </TextInfo>
          </TopRow>
          <Controls>
            <StyleButtonsRow
              onMouseLeave={() => {
                if (isDesktop) {
                  setHoverStyle(null)
                  setHoverColor(null)
                }
              }}
            >
              {styleKeys.map((styleKey) => (
                <StyleButton
                  key={styleKey}
                  selected={selectedStyle === styleKey}
                  type="button"
                  onClick={() => handleStyleChange(styleKey)}
                  onMouseEnter={() => {
                    if (isDesktop) {
                      setHoverStyle(styleKey)
                      setHoverColor(colorKeysByStyle[styleKey]?.[0] || null)
                    }
                  }}
                >
                  {styleKey}
                </StyleButton>
              ))}
            </StyleButtonsRow>
            <ColorRow>
              <Swatches
                onMouseLeave={() => {
                  if (isDesktop) {
                    setHoverStyle(null)
                    setHoverColor(null)
                  }
                }}
              >
                {colorsForSelectedStyle.map((colorKey) => (
                  <SwatchButton
                    key={colorKey}
                    selected={selectedColor === colorKey}
                    type="button"
                    aria-label={colorKey}
                    background={COLOR_TO_BACKGROUND[colorKey] || '#d9d9d9'}
                    onClick={() => {
                      Analytics.track(
                        `Clicked Cart ${calendarLineLabel} Snap Frame Cross Sell ${colorKey} Color Swatch CTA`
                      )
                      setSelectedColor(colorKey)
                      setHoverStyle(null)
                      setHoverColor(null)
                    }}
                    onMouseEnter={() => {
                      if (isDesktop) {
                        setHoverStyle(selectedStyle)
                        setHoverColor(colorKey)
                      }
                    }}
                  />
                ))}
              </Swatches>
              <ColorName>{selectedColor}</ColorName>
            </ColorRow>
          </Controls>
        </Content>
        <CtaCol>
          <PriceContainer hideOnMobile>
            <OriginalPrice>{FormattedCurrency(originalPrice)}</OriginalPrice>
            <DiscountedPrice>{FormattedCurrency(discountedPrice)}</DiscountedPrice>
          </PriceContainer>
          <AddToCartButton
            label="Get the Deal"
            adding={isButtonAdding}
            oos={false}
            handleClick={handleGetDealClick}
          />
        </CtaCol>
      </Card>
    </Wrapper>
  )
}

CalendarSnapFrameCrossSell.propTypes = {
  cartLines: PropTypes.arrayOf(PropTypes.object).isRequired,
  snapProducts: PropTypes.shape({
    calendar2: PropTypes.object,
    calendarMax: PropTypes.object,
  }).isRequired,
  variantImageMap: PropTypes.object,
  fallbackImageData: PropTypes.object,
  handleAddToCart: PropTypes.func.isRequired,
  adding: PropTypes.bool,
}

export default CalendarSnapFrameCrossSell
